import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Key, Mail, Zap, Globe, Server } from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const [settings, setSettings] = useState({
    default_llm_provider: 'openai',
    default_llm_model: 'gpt-5',
    openai_api_key: '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',
    anthropic_api_key: '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',
    proxy_enabled: false,
    proxy_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({
          ...prev,
          ...data
        }));
      }
    } catch (error) {
      console.error('Ошибка загрузки настроек:', error);
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        toast.success('Настройки успешно сохранены!');
        await loadSettings(); // Перезагружаем настройки
      } else {
        toast.error('Ошибка при сохранении настроек');
      }
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      toast.error('Ошибка соединения с сервером');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-white text-xl">Загрузка настроек...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-8" data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-white mb-2">Настройки</h1>
        <p className="text-gray-400">Управление системными параметрами</p>
      </div>

      <div className="max-w-4xl space-y-6">
        {/* API Keys */}
        <Card className="glass-panel border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Key className="w-5 h-5" />
              API Ключи
            </CardTitle>
            <CardDescription className="text-gray-400">
              Настройка ключей для LLM провайдеров
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300">OpenAI API Key</Label>
              <Input
                type="password"
                value={settings.openai_api_key || ''}
                onChange={(e) => setSettings({...settings, openai_api_key: e.target.value})}
                className="bg-white/5 border-white/20 text-white"
                placeholder="sk-..."
              />
            </div>
            <div>
              <Label className="text-gray-300">Anthropic API Key</Label>
              <Input
                type="password"
                value={settings.anthropic_api_key || ''}
                onChange={(e) => setSettings({...settings, anthropic_api_key: e.target.value})}
                className="bg-white/5 border-white/20 text-white"
                placeholder="sk-ant-..."
              />
            </div>
          </CardContent>
        </Card>

        {/* LLM Settings */}
        <Card className="glass-panel border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Zap className="w-5 h-5" />
              Настройки LLM
            </CardTitle>
            <CardDescription className="text-gray-400">
              Выбор провайдера и модели по умолчанию
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300">Провайдер по умолчанию</Label>
              <Select value={settings.default_llm_provider} onValueChange={(value) => setSettings({...settings, default_llm_provider: value})}>
                <SelectTrigger className="bg-white/5 border-white/20 text-white">
                  <SelectValue placeholder="Выберите провайдера" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/20">
                  <SelectItem value="openai" className="text-white">OpenAI</SelectItem>
                  <SelectItem value="anthropic" className="text-white">Anthropic (Claude)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-300">Модель по умолчанию</Label>
              <Select value={settings.default_llm_model} onValueChange={(value) => setSettings({...settings, default_llm_model: value})}>
                <SelectTrigger className="bg-white/5 border-white/20 text-white">
                  <SelectValue placeholder="Выберите модель" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/20">
                  <SelectItem value="gpt-5" className="text-white">GPT-5 (Latest)</SelectItem>
                  <SelectItem value="gpt-4o" className="text-white">GPT-4o</SelectItem>
                  <SelectItem value="gpt-4" className="text-white">GPT-4</SelectItem>
                  <SelectItem value="claude-4-5-sonnet-20250219" className="text-white">Claude 4.5 Sonnet</SelectItem>
                  <SelectItem value="claude-3-7-sonnet-20250219" className="text-white">Claude 3.7 Sonnet</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Proxy Settings */}
        <Card className="glass-panel border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Настройки Прокси (для работы в России)
            </CardTitle>
            <CardDescription className="text-gray-400">
              Подключение через прокси-сервер для обхода блокировок
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-gray-300">Включить прокси</Label>
                <p className="text-sm text-gray-500 mt-1">Использовать прокси для API запросов к LLM</p>
              </div>
              <Switch
                checked={settings.proxy_enabled}
                onCheckedChange={(checked) => setSettings({...settings, proxy_enabled: checked})}
              />
            </div>
            
            {settings.proxy_enabled && (
              <div>
                <Label className="text-gray-300">Proxy URL</Label>
                <Input
                  type="text"
                  value={settings.proxy_url || ''}
                  onChange={(e) => setSettings({...settings, proxy_url: e.target.value})}
                  className="bg-white/5 border-white/20 text-white"
                  placeholder="http://user:password@proxy-server:port"
                />
                <p className="text-xs text-gray-500 mt-2">
                  Формат: http://username:password@178.159.43.73:3128
                </p>
              </div>
            )}

            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-start gap-3">
                <Server className="w-5 h-5 text-blue-400 mt-0.5" />
                <div className="text-sm text-gray-300">
                  <p className="font-semibold text-blue-400 mb-1">Инструкция по настройке прокси:</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs text-gray-400">
                    <li>Включите "Включить прокси" выше</li>
                    <li>Введите URL вашего прокси-сервера</li>
                    <li>Пример: <code className="bg-black/30 px-1 rounded">http://aiagent:AiProxy2025!@178.159.43.73:3128</code></li>
                    <li>Нажмите "Сохранить настройки"</li>
                    <li>Прокси будет применен для всех LLM запросов</li>
                  </ol>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Email Notifications */}
        <Card className="glass-panel border-white/10 bg-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5" />
              Уведомления
            </CardTitle>
            <CardDescription className="text-gray-400">
              Настройка email уведомлений
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-gray-300">Email уведомления</Label>
                <p className="text-sm text-gray-500 mt-1">Получать уведомления о событиях системы</p>
              </div>
              <Switch
                checked={settings.email_notifications_enabled}
                onCheckedChange={(checked) => setSettings({...settings, email_notifications_enabled: checked})}
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {saving ? 'Сохранение...' : 'Сохранить настройки'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
